import React, { useMemo, useState } from "react";
import {
  MagnifyingGlassIcon,
  XCircleIcon,
  FunnelIcon,
  ArrowsUpDownIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  LockClosedIcon,
  InboxIcon,
  InboxStackIcon,
  UserCircleIcon,
  ChatBubbleLeftEllipsisIcon,
} from "@heroicons/react/24/outline";
import { INQUIRY_TYPES, SMART_VIEWS, TYPE_TONE } from "../constants";
import {
  classNames,
  formatRelative,
  statusOf,
  urgencyFor,
  urgencyTone,
  responseNeededSince,
  INQUIRY_SORT_OPTIONS,
} from "./utils";
import { htmlToPreview } from "../../../utils/sanitizeHtml";

// Secondary filter chips that sit on top of the smart-view (awaiting /
// replied / closed). These are client-side only — we derive them from the
// same inquiry shape already in memory — so they're cheap and additive.
const ACTIVITY_FILTERS = [
  { id: "all", label: "All", icon: InboxStackIcon },
  { id: "unreplied", label: "Unreplied", icon: InboxIcon, tone: "amber" },
  { id: "guest-replied", label: "Guest replied", icon: ChatBubbleLeftEllipsisIcon, tone: "amber" },
  { id: "team-replied", label: "Replied by team", icon: CheckCircleIcon, tone: "indigo" },
  { id: "open", label: "Open", icon: InboxIcon, tone: "emerald" },
  { id: "closed", label: "Closed", icon: LockClosedIcon, tone: "zinc" },
];

// Decide the last-message direction using messages[] first, responses[]
// fallback. Returns "guest", "team", or null.
function lastDirection(inquiry) {
  const messages = Array.isArray(inquiry?.messages) ? inquiry.messages : [];
  if (messages.length) {
    const last = messages[messages.length - 1];
    if (last?.direction === "inbound") return "guest";
    if (last?.direction === "outbound") return "team";
  }
  const responses = Array.isArray(inquiry?.responses) ? inquiry.responses : [];
  if (responses.length) {
    const last = responses[responses.length - 1];
    // If responder equals the guest's email/name we treat as guest.
    const responder = (last?.responder || "").toLowerCase();
    if (
      (inquiry?.email && responder === String(inquiry.email).toLowerCase()) ||
      (inquiry?.name && responder === String(inquiry.name).toLowerCase())
    ) {
      return "guest";
    }
    return "team";
  }
  return null;
}

function activityPredicate(filter) {
  if (!filter || filter === "all") return () => true;
  return (i) => {
    const dir = lastDirection(i);
    switch (filter) {
      case "unreplied":
        return !i.replied && !i.closed;
      case "guest-replied":
        // Guest has replied since the last team message — last direction is inbound
        // AND there's at least one team reply already.
        return dir === "guest" && (i.responses || []).some((r) => {
          const responder = (r?.responder || "").toLowerCase();
          const isGuest =
            (i.email && responder === String(i.email).toLowerCase()) ||
            (i.name && responder === String(i.name).toLowerCase());
          return !isGuest;
        });
      case "team-replied":
        return dir === "team" || i.replied;
      case "open":
        return !i.closed;
      case "closed":
        return Boolean(i.closed);
      default:
        return true;
    }
  };
}

function StatusPill({ inquiry }) {
  const status = statusOf(inquiry);
  const map = {
    new: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    replied: "bg-indigo-50 text-indigo-700 ring-indigo-200",
    closed: "bg-zinc-100 text-zinc-600 ring-zinc-200",
  };
  const label = { new: "New", replied: "Replied", closed: "Closed" }[status];
  return (
    <span
      className={classNames(
        "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1",
        map[status]
      )}
    >
      {label}
    </span>
  );
}

function TypeBadge({ type }) {
  const tone = TYPE_TONE[type] || TYPE_TONE.Other;
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium ring-1",
        tone.chip
      )}
    >
      <span className={classNames("inline-block h-1.5 w-1.5 rounded-full", tone.dot)} />
      {type || "Other"}
    </span>
  );
}

function DirectionDot({ inquiry }) {
  const dir = lastDirection(inquiry);
  if (!dir) return null;
  const isGuest = dir === "guest";
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1",
        isGuest
          ? "bg-amber-50 text-amber-800 ring-amber-200"
          : "bg-indigo-50 text-indigo-700 ring-indigo-200"
      )}
      title={isGuest ? "Guest sent the last message" : "Team sent the last message"}
    >
      <span
        className={classNames(
          "inline-block h-1.5 w-1.5 rounded-full",
          isGuest ? "bg-amber-500" : "bg-indigo-500"
        )}
      />
      {isGuest ? "Guest" : "Team"}
    </span>
  );
}

function Avatar({ name }) {
  const initials = (name || "?")
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("") || "?";
  return (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-50 to-amber-50 text-[12px] font-semibold text-zinc-700 ring-1 ring-zinc-200">
      {initials}
    </div>
  );
}

function UrgencyBar({ inquiry }) {
  const id = urgencyFor(inquiry);
  if (!id) return <span className="block w-1 bg-transparent" />;
  const tone = urgencyTone(id);
  return <span className={classNames("block w-1 rounded-l-xl", tone.bar)} />;
}

function LastActivityLine({ inquiry }) {
  const dir = lastDirection(inquiry);
  const responses = Array.isArray(inquiry.responses) ? inquiry.responses : [];
  const messages = Array.isArray(inquiry.messages) ? inquiry.messages : [];

  if (inquiry.closed) {
    return (
      <span className="truncate text-xs text-zinc-500">
        Closed · {formatRelative(inquiry.updatedAt || inquiry.respondedAt || inquiry.createdAt)}
      </span>
    );
  }
  if (dir === "guest") {
    return (
      <span className="truncate text-xs text-amber-700">
        Guest replied · {formatRelative(messages[messages.length - 1]?.timestamp || responses[responses.length - 1]?.respondedAt)}
      </span>
    );
  }
  if (dir === "team") {
    const last =
      messages[messages.length - 1]?.direction === "outbound"
        ? messages[messages.length - 1]
        : null;
    const respLast = responses[responses.length - 1];
    const responder = last?.from || respLast?.responder;
    const when =
      last?.timestamp || respLast?.respondedAt || inquiry.respondedAt || inquiry.updatedAt;
    return (
      <span className="truncate text-xs text-zinc-500">
        {responder ? (
          <>
            Replied by <span className="font-medium text-zinc-700">{shortName(responder)}</span>
          </>
        ) : (
          "Replied by team"
        )}{" "}
        · {formatRelative(when)}
      </span>
    );
  }
  return (
    <span className="truncate text-xs text-amber-700">
      Waiting · received {formatRelative(responseNeededSince(inquiry) || inquiry.createdAt)}
    </span>
  );
}

function shortName(value) {
  if (!value) return "—";
  const str = String(value);
  // Strip "Name <email>" pattern
  const bracket = str.match(/^([^<]+)</);
  if (bracket) return bracket[1].trim();
  if (str.includes("@")) return str.split("@")[0];
  return str;
}

function inquiryPreview(inquiry) {
  const messages = Array.isArray(inquiry.messages) ? inquiry.messages : [];
  if (messages.length) {
    const last = messages[messages.length - 1];
    const preview = htmlToPreview(last.html || last.body || "", { maxLength: 160 });
    if (preview) return preview;
  }
  const responses = Array.isArray(inquiry.responses) ? inquiry.responses : [];
  if (responses.length) {
    const last = responses[responses.length - 1];
    const preview = htmlToPreview(last.message || "", { maxLength: 160 });
    if (preview) return preview;
  }
  return htmlToPreview(inquiry.message || "", { maxLength: 160 });
}

function Row({ inquiry, selected, onSelect, onMarkReplied, onToggleClosed, onChangeType }) {
  const preview = inquiryPreview(inquiry);
  return (
    <div
      onClick={() => onSelect?.(inquiry._id)}
      className={classNames(
        "relative flex cursor-pointer items-stretch border-b border-zinc-100 transition-colors",
        selected ? "bg-indigo-50/70" : "bg-white hover:bg-zinc-50"
      )}
    >
      <UrgencyBar inquiry={inquiry} />
      <div className="flex min-w-0 flex-1 items-center gap-3 py-3 pl-3 pr-3">
        <Avatar name={inquiry.name} />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <p className="truncate text-sm font-semibold text-zinc-900">
              {inquiry.name || "Unnamed"}
            </p>
            <StatusPill inquiry={inquiry} />
            <TypeBadge type={inquiry.type} />
            <DirectionDot inquiry={inquiry} />
          </div>
          <p className="mt-0.5 truncate text-xs text-zinc-500">
            {inquiry.email || "no email"}
            {inquiry.phone ? <span className="mx-1 text-zinc-300">·</span> : null}
            {inquiry.phone || ""}
          </p>
          {inquiry.subject ? (
            <p className="mt-1 truncate text-xs font-medium text-zinc-800">{inquiry.subject}</p>
          ) : null}
          {preview ? (
            <p className="mt-0.5 line-clamp-1 text-xs text-zinc-500">{preview}</p>
          ) : (
            <p className="mt-0.5 line-clamp-1 text-xs italic text-zinc-400">(no message body)</p>
          )}
        </div>

        <div className="flex w-48 shrink-0 flex-col items-end gap-1 text-right">
          <span className="text-xs text-zinc-500" title={new Date(inquiry.createdAt).toString()}>
            {formatRelative(inquiry.createdAt)}
          </span>
          <LastActivityLine inquiry={inquiry} />
        </div>

        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
          <RowActions
            inquiry={inquiry}
            onMarkReplied={onMarkReplied}
            onToggleClosed={onToggleClosed}
            onChangeType={onChangeType}
          />
        </div>
      </div>
    </div>
  );
}

function RowActions({ inquiry, onMarkReplied, onToggleClosed, onChangeType }) {
  return (
    <div className="flex items-center gap-1">
      <select
        value={inquiry.type || "Other"}
        onChange={(e) => onChangeType?.(inquiry._id, e.target.value)}
        className="rounded-md border border-zinc-200 bg-white px-1.5 py-1 text-[11px] text-zinc-700 focus:border-indigo-500 focus:outline-none"
      >
        {INQUIRY_TYPES.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      {!inquiry.replied && !inquiry.closed ? (
        <button
          type="button"
          onClick={() => onMarkReplied?.(inquiry._id)}
          className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-1.5 py-1 text-[11px] font-medium text-indigo-700 hover:bg-indigo-100"
          title="Mark as replied without sending"
        >
          <CheckCircleIcon className="h-3 w-3" /> Replied
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => onToggleClosed?.(inquiry._id, !inquiry.closed)}
        className={classNames(
          "inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium",
          inquiry.closed
            ? "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
            : "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
        )}
        title={inquiry.closed ? "Reopen" : "Close inquiry"}
      >
        {inquiry.closed ? (
          <>
            <ArrowUturnLeftIcon className="h-3 w-3" /> Reopen
          </>
        ) : (
          <>
            <LockClosedIcon className="h-3 w-3" /> Close
          </>
        )}
      </button>
    </div>
  );
}

export default function InquiryLedger({
  inquiries,
  loading,
  view,
  search,
  onSearchChange,
  selectedId,
  onSelect,
  onMarkReplied,
  onToggleClosed,
  onChangeType,
  sortBy,
  onSortChange,
  onOpenTemplates,
}) {
  const [activity, setActivity] = useState("all");
  const viewMeta = SMART_VIEWS.find((v) => v.id === view);

  const filtered = useMemo(() => inquiries.filter(activityPredicate(activity)), [inquiries, activity]);
  const total = filtered.length;

  return (
    <section className="flex min-w-0 flex-1 flex-col bg-zinc-50">
      <header className="flex flex-col gap-2 border-b border-zinc-200 bg-white px-5 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <UserCircleIcon className="h-4 w-4 text-zinc-400" />
            <h2 className="text-sm font-semibold text-zinc-900">
              {viewMeta?.label || "Inquiries"}
            </h2>
            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-600">
              {total}
            </span>
          </div>

          <div className="relative ml-auto flex min-w-[280px] flex-1 items-center">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 h-4 w-4 text-zinc-400" />
            <input
              type="text"
              data-inq-search
              value={search}
              onChange={(event) => onSearchChange?.(event.target.value)}
              placeholder="Search name, email, phone, subject, message…"
              className="w-full rounded-lg border border-zinc-200 bg-white py-1.5 pl-9 pr-9 text-sm placeholder:text-zinc-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
            />
            {search ? (
              <button
                type="button"
                onClick={() => onSearchChange?.("")}
                className="absolute right-2 rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                aria-label="Clear search"
              >
                <XCircleIcon className="h-4 w-4" />
              </button>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2 py-1">
              <ArrowsUpDownIcon className="h-3.5 w-3.5 text-zinc-400" />
              <select
                value={sortBy || (view === "awaiting" ? "oldest" : "newest")}
                onChange={(e) => onSortChange?.(e.target.value || "newest")}
                className="border-none bg-transparent text-[11px] font-medium text-zinc-700 focus:outline-none"
                title="Sort inquiries"
              >
                {INQUIRY_SORT_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {onOpenTemplates ? (
              <button
                type="button"
                onClick={onOpenTemplates}
                className="rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-zinc-700 hover:bg-zinc-50"
                title="Manage response templates"
              >
                Templates
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
            <FunnelIcon className="h-3.5 w-3.5" />
            Filter
          </div>
          {ACTIVITY_FILTERS.map((f) => {
            const active = activity === f.id;
            const Icon = f.icon;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setActivity(f.id)}
                className={classNames(
                  "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 transition-colors",
                  active
                    ? "bg-zinc-900 text-white ring-zinc-900"
                    : "bg-white text-zinc-700 ring-zinc-200 hover:bg-zinc-50"
                )}
              >
                <Icon className="h-3 w-3" />
                {f.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        {loading && !inquiries.length ? (
          <div className="flex h-full items-center justify-center p-10 text-sm text-zinc-500">
            Loading inquiries…
          </div>
        ) : !filtered.length ? (
          <EmptyState view={view} activity={activity} />
        ) : (
          <div>
            {filtered.map((inquiry) => (
              <Row
                key={inquiry._id}
                inquiry={inquiry}
                selected={selectedId === inquiry._id}
                onSelect={onSelect}
                onMarkReplied={onMarkReplied}
                onToggleClosed={onToggleClosed}
                onChangeType={onChangeType}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function EmptyState({ view, activity }) {
  if (activity && activity !== "all") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-1 p-12 text-center">
        <p className="text-sm font-semibold text-zinc-700">No inquiries match this filter.</p>
        <p className="max-w-xs text-xs text-zinc-500">
          Try "All" to clear the activity filter, or switch to a different view.
        </p>
      </div>
    );
  }
  const copy = {
    awaiting: {
      title: "No inquiries waiting on a reply.",
      body: "You're fully caught up. Nice.",
    },
    replied: {
      title: "No replied inquiries yet.",
      body: "Reply to something from Awaiting reply and it'll land here.",
    },
    closed: {
      title: "No closed inquiries.",
      body: "Closed inquiries are archived but kept for reference.",
    },
    all: {
      title: "No inquiries at all.",
      body: "New inbound messages appear here automatically.",
    },
  }[view] || { title: "No results", body: "Try clearing filters or search." };

  return (
    <div className="flex h-full flex-col items-center justify-center gap-1 p-12 text-center">
      <p className="text-sm font-semibold text-zinc-700">{copy.title}</p>
      <p className="max-w-xs text-xs text-zinc-500">{copy.body}</p>
    </div>
  );
}
